import React from "react"
import { Button } from "antd"
import styled from "styled-components"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCookie } from "@fortawesome/pro-regular-svg-icons"

const Content = styled.div`
  p {
    font-size: 1.3rem;
    margin: 0 0 0.5rem 0;
  }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 12000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: auto;
`

const Banner = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  border-radius: 0;
  padding: 16px;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.12);
  z-index: 12001;
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const Inner = styled.div`
  width: 100%;
  max-width: 1100px; /* center constraint - adjust as needed */
  margin: 0 auto;    /* centers the inner content */
  padding: 0 16px;   /* ensure spacing on small screens */
  display: flex;
  flex-direction: column;
  align-items: stretch; /* keep content left-justified */
`

const InnerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

interface ICookiePermissionModalProps {
  visible: boolean
  setVisible: (value: boolean) => void
  acceptCookies: () => void
}

/**
 * Bottom-fixed cookie permission banner.
 * - dims and blocks interaction with the page while visible
 * - mounted to document.body via portal
 * - preserves existing acceptCookies() behavior
 */
export default function CookiePermissionModal({
  visible,
  setVisible,
  acceptCookies,
}: ICookiePermissionModalProps) {
  if (!visible) return null

  const onAccept = () => {
    try {
      acceptCookies()
    } finally {
      setVisible(false)
    }
  }

  return createPortal(
    <Overlay role="dialog" aria-modal="true" aria-label="Cookie Permission Banner">
      <Banner>
        <Inner>
          <InnerRow>
            <div style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
              <FontAwesomeIcon icon={faCookie} aria-hidden="true" />
              <strong>Cookie Permission</strong>
            </div>
            <Actions>
              <Button key="cancel" type="link" onClick={() => setVisible(false)}>Dismiss</Button>
              <Button key="ok" type="primary" onClick={onAccept}>Accept Cookies</Button>
            </Actions>
          </InnerRow>

          <Content style={{ textAlign: "left" }}>
            <p>Hi there 👋,</p>
            <p>
              Please accept cookies to help us keep your account secure and
              provide you with the best experience on our site.
            </p>
            <p>
              FYI if you're on a public computer you should be sure to sign out of
              the clubhouse by clicking the log out button when you're done!
            </p>
          </Content>
        </Inner>
      </Banner>
    </Overlay>,
    document.body,
  )
}